import { z } from 'zod';

/**
 * Bundle format version. Bumped whenever the envelope shape changes in a
 * backwards-incompatible way - the import endpoint rejects unknown versions.
 */
export const SEED_BUNDLE_VERSION = 1;

/**
 * Scope passed into every seeds operation (inventory, export, import).
 * The caller's active organization id is required - seeds are org-scoped.
 */
export const SeedScopeSchema = z.object({
  organizationId: z.string(),
});
export type SeedScope = z.infer<typeof SeedScopeSchema>;

export const SeedExportItemSchema = z.object({
  /**
   * Stable, human-readable item name. Phase 1 imports still use this as the
   * create/update key within a kind, and refs resolve via `{ kind, name }`.
   */
  name: z.string(),
  /**
   * Id-less provider-owned payload used to recreate or update the item on the
   * target. The seed runner treats this as opaque data.
   */
  portable: z.unknown(),
  /**
   * Provider-owned natural identity metadata for future conflict detection.
   * This may be stricter than `name` (for example, including org/kind or other
   * natural-key parts), but Phase 1 import behavior still uses `name`.
   */
  logicalKey: z.string().optional(),
});
export type SeedExportItemPayload = z.infer<typeof SeedExportItemSchema>;

/**
 * Bundle-level metadata introduced in Phase 1 (PRD-2). Captures the
 * export-time scope and optional provenance fields.
 */
export const SeedBundleMetadataSchema = z.object({
  /** Organization the bundle was exported from. */
  scope: SeedScopeSchema,
  /** User or system that triggered the export (optional provenance). */
  exportedBy: z.string().optional(),
});
export type SeedBundleMetadata = z.infer<typeof SeedBundleMetadataSchema>;

/** Per-kind dependency snapshot captured at export time. */
export const SeedKindDescriptorSchema = z.object({
  /** Stable provider/resource kind (matches `SeedProvider.kind`). */
  kind: z.string(),
  /** Provider's declared dependencies at export time. */
  dependencies: z.array(z.string()),
});
export type SeedKindDescriptor = z.infer<typeof SeedKindDescriptorSchema>;

export const SeedBundleSchema = z.object({
  version: z.literal(SEED_BUNDLE_VERSION),
  exportedAt: z.number(),
  /** Kinds in dependency-safe order (dependencies first). */
  kinds: z.array(z.string()),
  /** Per-kind ordered list of portable items. */
  items: z.record(z.string(), z.array(SeedExportItemSchema)),
  /**
   * Bundle-level metadata (Phase 1, PRD-2). Optional for backward compat
   * with v1 bundles that lack it - the normalizer fills defaults.
   */
  metadata: SeedBundleMetadataSchema.optional(),
  /**
   * Per-kind descriptors capturing dependency order at export time (Phase 1,
   * PRD-2). `kind` is the provider kind; there is no separate provider-kind
   * field. Optional for backward compat.
   */
  kindDescriptors: z.array(SeedKindDescriptorSchema).optional(),
});
export type SeedBundle = z.infer<typeof SeedBundleSchema>;

/**
 * Plaintext seed secret values supplied alongside an import bundle, keyed by
 * the required credential key in the bundle response.
 */
export type SeedSecretValues = Record<string, string>;

/**
 * Credential input file envelope (cleartext JSON wrapper).
 * The ciphertext is an `enc:v1:<iv-b64>:<tag-b64>:<ct-b64>` GCM envelope.
 */
export const SeedCredentialFileEnvelopeSchema = z.object({
  version: z.literal(1),
  alg: z.literal('aes-256-gcm'),
  ciphertext: z.string(),
});
export type SeedCredentialFileEnvelope = z.infer<typeof SeedCredentialFileEnvelopeSchema>;
